import {
  Button,
  ButtonProps,
  Icon,
  IconName,
  Option,
  OptionList,
  Popover,
  Select,
  SelectProps,
  useTheme
} from "@introist/react-foundation/v2";
import { EmployeeField, useEmployeeFields } from "../hooks/useEmployeeFields";
import { useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { compact } from "lodash";
import { Form, useForms } from "../../forms/useForms";
import { useFormFields } from "../../forms/useFormFields";
import { CompanyDataSelectorList } from "../../companydata/CompanyDataSelectorList";

const IconBackground = styled.span`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: var(--rounding-small);
  background: var(--palette-purple-ghosted);

  svg {
    width: 12px;
    height: 12px;
    fill: var(--palette-purple-default) !important;
  }
`;

export type Category = {
  key: string;
  title: string;
  options: Option[];
  icon?: IconName;
};

export const SelectorIcon = ({ icon, onClick }: { icon: IconName; onClick?: () => unknown }) => (
  <IconBackground onClick={onClick}>
    <Icon name={icon} />
  </IconBackground>
);

export const EmployeeFieldSelector = ({
  onSelect,
  onAdd,
  fieldTypes,
  ...rest
}: {
  onSelect: (path: string) => void;
  onAdd?: (search: string) => void;
  fieldTypes?: string[];
} & Omit<ButtonProps, "onClick">) => {
  const [ref, setRef] = useState<HTMLElement | null>(null);
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        ref={setRef}
        onClick={() => setOpen(true)}
        endAdornment={<Icon name="attribute" />}
        variant="outlined"
        children="Select Field"
        {...rest}
      />
      <EmployeeFieldSelectorPopover
        popoverRef={ref}
        open={open}
        setOpen={setOpen}
        onSelect={onSelect}
        onAdd={onAdd}
        fieldTypes={fieldTypes}
      />
    </>
  );
};

export const EmployeeFieldSelectorPopover = ({
  open,
  setOpen,
  onSelect,
  onAdd,
  popoverRef,
  fieldTypes,
  preSearch,
  customCategories,
  tagMode,
  formsEnabled = true,
  companyDataEnabled = true,
  employeeSelectMode
}: {
  open?: boolean;
  setOpen: (open: boolean) => void;
  popoverRef: HTMLElement | null;
  onSelect: (fieldPath: string, title: string) => void;
  onAdd?: (search: string) => void;
  fieldTypes?: string[];
  preSearch?: string;
  tagMode?: boolean; // If enabled, value in onChange will be in {{value}} format (except for customCategories)
  customCategories?: Category[];
  formsEnabled?: boolean;
  companyDataEnabled?: boolean;
  employeeSelectMode?: boolean;
}) => {
  const { theme } = useTheme();
  const { fields } = useEmployeeFields();

  const [parentField, setParentField] = useState<EmployeeField | null>(null);
  const [openCategory, setOpenCategory] = useState<string | null>(null);

  const doSelect = useCallback(
    (path: string, title: string, custom = false) => {
      const modPath = tagMode && !custom ? `{{${path}}}` : path;

      onSelect(modPath, title);
      setOpenCategory(null);
      setOpen(false);
    },
    [onSelect, setOpen, tagMode]
  );

  const options = useMemo(() => {
    if (!fields) return [];

    const available = fieldTypes
      ? fields.filter(f => [...fieldTypes, "employee"].includes(f.type))
      : fields;

    const usable = parentField ? available.filter(f => f.type !== "employee") : available;
    const all = usable
      .filter(
        f => parentField || !preSearch || f.title.toLowerCase().includes(preSearch.toLowerCase())
      )
      .map(f => ({
        key: f.key,
        title: f.title,
        startAdornment:
          f.type === "employee" ? (
            <IconBackground>
              <Icon name="person" />
            </IconBackground>
          ) : (
            <IconBackground>
              <Icon name="attribute" />
            </IconBackground>
          ),
        onClick: () => {
          if (f.type === "employee" && !employeeSelectMode) {
            setParentField(f);
          } else {
            const path = parentField ? `${parentField.key}.${f.key}` : f.key;
            const title = parentField ? `${parentField.title} - ${f.title}` : f.title;

            doSelect(path, title);
          }
        }
      }));

    const customCats = (customCategories ?? []).map(c => ({
      key: c.key,
      title: c.title,
      startAdornment: <SelectorIcon icon={c.icon ?? "attributeGroup"} />,
      onClick: () => setOpenCategory(c.key)
    }));

    return compact([
      !parentField && formsEnabled
        ? {
            key: "forms",
            title: "Forms",
            startAdornment: (
              <IconBackground>
                <Icon name="form" />
              </IconBackground>
            ),
            onClick: () => setOpenCategory("forms")
          }
        : null,
      !parentField && companyDataEnabled
        ? {
            key: "companydata",
            title: "Company Data",
            startAdornment: <SelectorIcon icon="office" />,
            onClick: () => setOpenCategory("roles")
          }
        : null,
      ...(!parentField ? customCats : []),
      ...all
    ]);
  }, [
    fields,
    parentField,
    fieldTypes,
    preSearch,
    customCategories,
    doSelect,
    companyDataEnabled,
    formsEnabled,
    employeeSelectMode
  ]);

  const openCustomCat = useMemo(
    () => customCategories?.find(c => c.key === openCategory),
    [customCategories, openCategory]
  );

  return (
    <Popover
      referenceElement={popoverRef}
      open={open}
      onClose={() => setOpen(false)}
      attachToRef={false}
    >
      {openCategory === "forms" && (
        <FormFieldSelectorList
          onBack={() => setOpenCategory(null)}
          onSelect={doSelect}
          fieldTypes={fieldTypes}
        />
      )}
      {openCategory === "roles" && (
        <CompanyDataSelectorList
          onBack={() => setOpenCategory(null)}
          onSelect={doSelect}
          fieldTypes={fieldTypes}
        />
      )}
      {openCustomCat && (
        <OptionList
          actions={[
            {
              key: "back",
              title: openCustomCat.title,
              startAdornment: <Icon name="arrowLeft" color={theme.palette.primary.default} />,
              onClick: () => setOpenCategory(null)
            }
          ]}
          searchable
          options={openCustomCat.options}
          onItemClick={opt => {
            doSelect(opt.key, opt.title ?? opt.key, true);
          }}
          clearSearchOnOptionsChange
        />
      )}

      {!openCategory && (
        <OptionList
          actions={compact([
            parentField
              ? {
                  key: "back",
                  title: parentField.title,
                  startAdornment: <Icon name="arrowLeft" color={theme.palette.primary.default} />,
                  onClick: () => setParentField(null)
                }
              : null
          ])}
          searchable
          onEnter={search => {
            if (onAdd) {
              onAdd(search);
              setOpen(false);
            }
          }}
          options={options}
          clearSearchOnOptionsChange
        />
      )}
    </Popover>
  );
};

export const EmployeeAsFieldSelector = (props: Omit<SelectProps, "options">) => {
  const { fields } = useEmployeeFields();

  const options = useMemo(() => {
    if (!fields) return [];
    return [{ key: "id", title: "Employee" }, ...fields.filter(f => f.type === "employee")].map(
      f => ({
        key: f.key === "id" ? "id" : `${f.key}.id`,
        title: f.title,
        startAdornment: (
          <IconBackground>
            <Icon name="person" />
          </IconBackground>
        )
      })
    );
  }, [fields]);

  return (
    <Select
      element="button"
      variant="outlined"
      placeholder="Select Employee"
      startAdornment={
        <IconBackground>
          <Icon name="person" />
        </IconBackground>
      }
      options={options}
      attachToRef={false}
      {...props}
    />
  );
};

export const FormFieldSelectorList = ({
  onBack,
  onSelect,
  fieldTypes
}: {
  onBack?: () => unknown;
  onSelect: (path: string, title: string) => void;
  fieldTypes?: string[];
}) => {
  const { theme } = useTheme();

  const { forms } = useForms();

  const [selectedForm, setSelectedForm] = useState<Form | null>(null);

  const { formFields } = useFormFields(selectedForm?.id ?? "", { enabled: !!selectedForm });

  const options = useMemo(() => {
    if (!selectedForm) {
      return (forms ?? []).map(f => ({
        key: f.id,
        title: f.title,
        startAdornment: <SelectorIcon icon="form" />,
        onClick: () => setSelectedForm(f)
      }));
    }

    const summaryOption = {
      key: `forms.${selectedForm.id}.summary`,
      title: "Summary",
      startAdornment: <SelectorIcon icon="description" />,
      onClick: () => {
        onSelect(`forms.${selectedForm.id}.summary`, `${selectedForm.title} - Summary`);
        setSelectedForm(null);
      }
    };

    const fieldOptions = (formFields ?? [])
      .filter(f => !fieldTypes || fieldTypes.includes(f.type))
      .map(f => ({
        key: `forms.${selectedForm.id}.${f.id}`,
        title: f.shortTitle ?? f.title,
        startAdornment: <SelectorIcon icon="attribute" />,
        onClick: () => {
          onSelect(
            `forms.${selectedForm.id}.${f.id}`,
            `${selectedForm.title} - ${f.shortTitle ?? f.title}`
          );
          setSelectedForm(null);
        }
      }));

    return [summaryOption, ...fieldOptions];
  }, [selectedForm, onSelect, forms, formFields, fieldTypes]);

  return (
    <OptionList
      actions={compact([
        selectedForm
          ? {
              key: "back",
              title: selectedForm.title,
              startAdornment: <Icon name="arrowLeft" color={theme.palette.primary.default} />,
              onClick: () => setSelectedForm(null)
            }
          : null,
        onBack && !selectedForm
          ? {
              key: "back",
              title: "Back",
              startAdornment: <Icon name="arrowLeft" color={theme.palette.primary.default} />,
              onClick: () => {
                setSelectedForm(null);
                onBack();
              }
            }
          : null
      ])}
      searchable
      options={options}
      clearSearchOnOptionsChange
    />
  );
};
